<template>
  <StandardEmptyPageContainer>
    <v-row no-gutters class="mb-4">
      <v-col cols="12">
        <HeaderWidget header-text="Help Center" type="Welcome" />
      </v-col>
    </v-row>
    <StandardCardContainer radius="small">
      <template #title>Frequently Asked Questions</template>
      <v-row class="mt-10">
        <v-col cols="12" lg="2" class="text-left ml-4">
          <h2>About</h2>
        </v-col>
        <v-col cols="12" lg="9">
          <v-expansion-panels variant="default" multiple>
            <v-expansion-panel elevation="0">
              <v-expansion-panel-title
                expand-icon="mdi-plus"
                collapse-icon="mdi-minus"
              >
                What is the Norwegian Transparency Act?
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <HelpTexts text-version="aboutTheAct" />
              </v-expansion-panel-text>
            </v-expansion-panel>
            <v-expansion-panel elevation="0">
              <v-expansion-panel-title
                expand-icon="mdi-plus"
                collapse-icon="mdi-minus"
              >
                How to get started with Transparency Chain?
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <HelpTexts text-version="aboutTransparencyChain" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row class="mt-10" v-for="section in faq">
        <v-col cols="12" lg="2" class="text-left ml-4">
          <h2>{{ section.section }}</h2>
        </v-col>
        <v-col cols="12" lg="9">
          <v-expansion-panels variant="default" multiple>
            <v-expansion-panel v-for="qa in section.questions" elevation="0">
              <v-expansion-panel-title
                expand-icon="mdi-plus"
                collapse-icon="mdi-minus"
              >
                {{ qa.question }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p v-html="qa.answer" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </StandardCardContainer>
    <StandardCardContainer radius="small" class="fixed-width-card mt-4">
      <template #title>Didn't find your answer?</template>
      <v-row>
        <v-col class="text-center">
          <AppkitIcon icon="icon-email-outline" size="xl" class="mt-4" />
        </v-col>
        <v-col class="text-left">
          If you are having trouble, please contact us at
          <a href="mailto:transparencychain@pwc.com"
            >transparencychain@pwc.com</a
          >.
        </v-col>
      </v-row>
    </StandardCardContainer>
  </StandardEmptyPageContainer>
</template>

<script setup lang="ts">
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";
import AppkitIcon from "@/components/custom/AppkitIcon.vue";
import HeaderWidget from "@/components/widgets/HeaderWidget.vue";
import HelpTexts from "@/views/help-view/HelpTexts.vue";

const faq = [
  {
    section: "Login",
    questions: [
      {
        question: "I am not receiving an email with the login token.",
        answer:
          "If you haven't received the login token, it’s possible that you have used the wrong email to log in with or that you \
          are not registered as a user yet. Please double check for misspelling and if the correct email is being used.",
      },
    ],
  },
  {
    section: "Dashboard",
    questions: [
      {
        question:
          "What does the list of countries mean in the boxes \“Labour and Human Rights\” and \“Health and Safety?\”",
        answer:
          "The Labour and Human Rights box identifies the five countries with the highest risk as per an average score \
          of Global Rights Index 2022 and Labour Rights Index 2022. This is based on the countries of operation for the \
          companies whose questionnaire responses were flagged.<br><br>\
          The Health and Safety box identifies the same using the Labour Rights Index 2022, since it includes data on safe \
          work and decent working hours amongst other topics.<br><br>\
          The list can also include low and medium risk countries if not all are high risk countries.",
      },
    ],
  },
  {
    section: "Users",
    questions: [
      {
        question: "How do I add a new user?",
        answer:
          "In the left menu bar, choose the <strong>Users</strong> section. Click the <strong>Add user</strong> button on \
          the right side. Fill in the name and email address of the person you want to add. Finally click the <strong>Add</strong> button.",
      },
    ],
  },
  {
    section: "My Suppliers",
    questions: [
      {
        question: "How do I add suppliers?",
        answer:
          "In the left menu bar, choose the <strong>My Suppliers</strong> section. Click the <strong>Add supplier</strong> \
          button and fill in the company name and email address. Select whether the company is a supplier or a business partner \
          and press the <strong>Add</strong> button.",
      },
      {
        question: "How do I delete a supplier/business partner?",
        answer:
          "In the left menu bar, choose the <strong>My Suppliers</strong> section. On the right side, you will find the \
          delete column with a trash can icon. Click the <strong>trash can icon</strong> to delete the appropriate supplier.",
      },
      {
        question: "How do I export a supplier sheet?",
        answer:
          "In the left menu bar, choose the <strong>My Suppliers</strong> section. Choose <strong>Export</strong> and \
          choose either an Excel file or a CSV file to be downloaded.",
      },
      {
        question: "How do I import a supplier sheet?",
        answer:
          "In the left menu bar, choose the <strong>My Suppliers</strong> section. Select <strong>Import</strong>, \
          download our template and fill it out, select your file and then select <strong>Upload</strong>. You must use \
          our template when uploading using Excel- or CSV-format. <br>You can also add new suppliers/business partners \
          individually by choosing <strong>Add Supplier</strong>.",
      },
      {
        question: "What defines a supplier/supply chain?",
        answer:
          "Transparency Act § 3 - By supply chain it means <strong>anyone in the chain of suppliers and subcontractors who \
            supply or produce goods, services or other input factors</strong> that are included in a company's provision of \
            services or production of goods from the raw material stage to the finished product. Input factors include, but \
            are not limited to, raw materials, components, services and transportation services.",
      },
      {
        question: "What defines a business partner?",
        answer:
          "§ 3 - By business partner it means anyone who <strong>supplies goods or services directly to the business</strong>, \
          but who is not part of the supply chain. <br>Typical business partners include the cleaning company that cleans your \
          office, your marketing agency and the company providing you with office chairs.",
      },
      {
        question:
          "Why does Transparency Chain distinguish between suppliers and business partners?",
        answer:
          "The Transparency Act’s due diligence requirement differentiates between suppliers and business partners. <br>Suppliers: \
          Enterprises must conduct a human rights  due diligence of all units in their supply chain, as far down as possible and proportional. \
          <br>Business partners: Enterprises must only conduct a human rights due diligence of the business partner itself and not this partner’s sub-suppliers.",
      },
    ],
  },
  {
    section: "Questionnaires",
    questions: [
      {
        question:
          "How do I send a questionnaire to my supplier or business partner?",
        answer:
          "In the left menu bar, choose the <strong>Questionnaires</strong> tab. Click the name of the questionnaire you want to \
          forward, and then click the <strong>Send</strong> button in the upper right corner. The new window will show you the list \
          of your suppliers and business partners. Select a <strong>date</strong> to request that your suppliers respond by. Simply \
          <strong>tick</strong> off the supplier(s) or business partner(s) you wish to send the questionnaires to. Press the \
          <strong>Preview email text</strong> button to preview the email text that will be sent. Click the <strong>Send</strong> \
          button on the lower right side to complete the process.",
      },
      {
        question: "Where can I see my received questionnaires?",
        answer:
          "In the left menu bar, choose the <strong>Questionnaires</strong> tab and then the <strong>Received Questionnaires</strong> \
          tab. This page will display all questionnaires received, who has sent them and also the progression status.",
      },
      {
        question: "Where can I view my answers for a questionnaire?",
        answer:
          "In the left menu bar, choose the <strong>Questionnaires</strong> tab and then the <strong>Received Questionnaires</strong> \
          tab. Choose the appropriate questionnaire name and click the <strong>View answers</strong> button on the right side.",
      },
      {
        question: "How can I check the recipients of my questionnaires?",
        answer:
          "In the left menu bar, choose the <strong>Questionnaires</strong> tab and then pick the appropriate questionnaire. In the \
          site overview you will find the recipient list under <strong>Recipients</strong>.",
      },
      {
        question: "What is Tier level?",
        answer:
          "Tier 1 Suppliers: These are the first level of suppliers to the company mapping its supply chain in Transparency Chain. \
          <br>Tier 2 suppliers: These are suppliers to the tier 1 suppliers. <br>Tier 3 suppliers: These are suppliers to the tier 2 suppliers. \
          <br>These tiers extend further depending on the levels of suppliers or subcontractors needed in the supply chain to create the product or service.",
      },
    ],
  },
];
</script>

<style scoped lang="scss">
.fixed-width-card {
  width: 405px;
}
</style>
